import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, CreditCard, Clock, AlertCircle, Check, X, Wallet, TrendingUp, ChevronRight } from 'lucide-react';

const BankingDemo = () => {
  const [balance] = useState(1250.75);
  const [showAI, setShowAI] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const mcdonaldsPrice = 8.99;

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const transactions = [
    { id: 1, name: 'Amazon', amount: -45.99, date: '03/19/2024', icon: <CreditCard size={20} /> },
    { id: 2, name: 'Salary Deposit', amount: 1500.00, date: '03/18/2024', icon: <Wallet size={20} /> },
    { id: 3, name: 'Uber', amount: -24.50, date: '03/18/2024', icon: <CreditCard size={20} /> },
    { id: 4, name: 'Spotify', amount: -9.99, date: '03/17/2024', icon: <CreditCard size={20} /> },
  ];

  const handlePurchaseCheck = async () => {
    setShowAI(true);
    setAnalyzing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setAnalyzing(false);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 relative">
      {/* Banking Interface */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-6 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-white">My Account</h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">{currentTime}</span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
              Checking <span className="text-xs">•••</span>4321
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 dark:text-gray-400">Available Balance</p>
            <motion.p 
              className="text-3xl font-bold text-[#68A511]"
              initial={{ scale: 1 }}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              ${balance.toFixed(2)}
            </motion.p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={18} className="text-[#68A511]" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Monthly Spend</span>
            </div>
            <p className="text-xl font-bold text-gray-800 dark:text-white">$847.32</p>
          </div>
          {/* Add more quick stats here if needed */}
        </div>

        {/* Recent Transactions */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-300">Recent Transactions</h4>
          <div className="space-y-3">
            {transactions.map(transaction => (
              <motion.div 
                key={transaction.id} 
                className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer group"
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                    {transaction.icon}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 dark:text-white">{transaction.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{transaction.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <p className={`font-medium ${transaction.amount > 0 ? 'text-green-500' : 'text-gray-800 dark:text-white'}`}>
                    {transaction.amount > 0 ? '+' : ''}{transaction.amount.toFixed(2)}
                  </p>
                  <ChevronRight size={16} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Purchase Check Button */}
        <motion.button
          onClick={handlePurchaseCheck}
          className="w-full bg-[#68A511] text-white py-3 rounded-lg font-medium hover:bg-[#527f0e] transition-colors relative overflow-hidden"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Check McDonald's Purchase ($8.99)
        </motion.button>
      </motion.div>

      {/* Green Guard AI Pop-up */}
      <AnimatePresence>
        {showAI && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="absolute top-1/2 right-4 transform -translate-y-1/2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-[#68A511]/10 rounded-lg">
                  <DollarSign size={20} className="text-[#68A511]" />
                </div>
                <h4 className="font-semibold text-gray-800 dark:text-white">Pocket</h4>
              </div>
              <button 
                onClick={() => setShowAI(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>

            {analyzing ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#68A511]" />
                <p className="text-gray-600 dark:text-gray-400">Analyzing your finances...</p>
              </div>
            ) : (
              <motion.div 
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-green-100 dark:bg-green-900 rounded-full">
                    <Check size={16} className="text-green-600 dark:text-green-400" />
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    You can afford McDonald's! This $8.99 purchase fits within your daily budget.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-blue-100 dark:bg-blue-900 rounded-full">
                    <AlertCircle size={16} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    Remember: You've spent $85 on dining this month. Consider home-cooked meals to stay within your monthly target.
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BankingDemo; 